import {Injectable} from '@angular/core';
import {OneTimeConfirmationDialogService}from '../../../../dialogs/one-time-confirmation-dialog/service/one-time-confirmation-dialog.service';
import {LicenseFeaturesService} from '../../../../services/common/license-features/license-features.service';
import {OfflinePeerConnectionApiService} from '../../../../services/api/network/offline/peer-connection/offline-peer-connection-api.service';
import {UserPrivilegeService} from '../../../../services/common/user-privilege/user-privilege.service';
import {DataItemToVisualsMappersOfflineService} from '../data-item-to-visuals-mappers-offline/data-item-to-visuals-mappers-offline.service';
import {PeerConnectionApiService} from '../../../../services/api/peer-connection/peer-connection-api.service';
import {OfflineNetworkService} from '../../offline/network/offline-network.service';
import {OfflineNodeService} from '../../offline/node/offline-node.service';
import {OfflinePeerConnectionService} from '../../offline/peer-connection/offline-peer-connection.service';
import {OfflineVoipPeerService} from '../../offline/voip-peer/offline-voip-peer.service';
import {OfflineConnectionService} from '../../offline/connection/offline-connection.service';
import {OfflineMapService} from '../../map/offline-map/offline-map.service';

@Injectable({
  providedIn: 'root'
})
export class OfflineDiagramContextMenuService {

  constructor(private oneTimeConfirmationDialogService: OneTimeConfirmationDialogService,
              private offlineNetworkService: OfflineNetworkService,
              private offlineNodeService: OfflineNodeService,
              private offlinePconService: OfflinePeerConnectionService,
              private offlineVoipPeerService: OfflineVoipPeerService,
              private offlineConnectionService: OfflineConnectionService,
              private licenseFeaturesService: LicenseFeaturesService,
              private peerConnectionsApiService: PeerConnectionApiService,
              private dataItemToVisualsMappersOfflineService: DataItemToVisualsMappersOfflineService,
              private offlinePeerConnectionApiService: OfflinePeerConnectionApiService,
              private offlineMapService: OfflineMapService,
              private userPrivilege: UserPrivilegeService) {
  }


  prepareContextMenuActions(diagramApi, clickedElement): void {
    let actionsObject;
    const diagramElement = clickedElement;

    const updateActionsSupport = () => {

      this.licenseFeaturesService.isFeatureSupported('testRoute').subscribe((isFeatureSupported) => {
        actionsObject.peerConnection.some((action) => {
          if (action.actionName === 'testRoute') {
            action.visible = isFeatureSupported;
          }

          return action.actionName === 'testRoute';
        });

        actionsObject.virtualPCon.some((action) => {
          if (action.actionName === 'testRoute') {
            action.visible = isFeatureSupported;
          }
          return action.actionName === 'testRoute';
        });
      });

      if (diagramElement.shapeType === 'node') {
        if (diagramElement.dataItem.productType !== 'THIRD_PARTY') {
          actionsObject.currentElement = 'node';
          updateNodeActionsSupport();
        } else {
          actionsObject.currentElement = 'thirdPartyNode';
          updateThirdPartyActionsSupport();
        }
      } else if (diagramElement.shapeType === 'voipPeer') {
        actionsObject.currentElement = 'voipPeer';
        updateVoipPeerActionsSupport();
      } else if (diagramElement.shapeType === 'cluster') {
        actionsObject.currentElement = 'cluster';
        // no actions to check
      } else if (diagramElement.connectionType === 'connection') {
        actionsObject.currentElement = 'connection';
        // no actions to check
      } else if (diagramElement.connectionType === 'peerConnection') {
        if (diagramElement.dataItem.peerConnectionNodeType === 'Virtual') {
          actionsObject.currentElement = 'virtualPCon';
          updateVirtualPConActionsSupport();
        } else {
          actionsObject.currentElement = 'peerConnection';
          updatePConActionsSupport();
        }
      } else if (diagramElement.connectionType === 'cluster') {
        actionsObject.currentElement = 'edgesCluster';
      }
    };

    const exportNodeToTopology = () => {
      this.offlineNodeService.exportNodeToTopology(diagramElement.dataItem.id);
    };

    const editNode = () => {
      if (diagramElement.dataItem.productType === 'THIRD_PARTY') {
        this.offlineNodeService.editThirdPartyDevice(diagramElement.dataItem.id);
      } else {
        this.offlineNodeService.editNode(diagramElement.dataItem.id);
      }
    };

    const addConnection = () => {
      if (diagramElement.dataItem.productType !== 'THIRD_PARTY') {
        this.offlineConnectionService.addConnection(diagramElement.dataItem.id, undefined);
      } else {
        this.offlineConnectionService.addConnection(undefined, diagramElement.dataItem.id);
      }
    };

    const lockNode = () => {
      this.offlineNodeService.lockNode(diagramElement.dataItem.id);
    };

    const unlockNode = () => {
      this.offlineNodeService.unlockNode(diagramElement.dataItem.id);
    };

    const deleteNode = () => {
      this.offlineNodeService.deleteNode(diagramElement.dataItem);
    };

    const addPCon = () => {
      this.offlinePconService.addVirtualPCon(diagramElement.dataItem, null);
    };

    const editVoipPeer = () => {
      this.offlineVoipPeerService.editVoipPeer(diagramElement.dataItem.id);
    };

    const deleteVoipPeer = () => {
      this.offlineVoipPeerService.deleteVoipPeer(diagramElement.dataItem);
    };

    const editConnection = () => {
      this.offlineConnectionService.editConnection(diagramElement.dataItem.id);
    };

    const deleteConnection = () => {
      this.offlineConnectionService.deleteConnection(diagramElement.dataItem);
    };

    const dragConnection = () => {
      this.oneTimeConfirmationDialogService.showPopup('DragConnectionExplanation',
        'Click and drag the mouse from one element to the other.',
        () => {
          diagramApi.addEdgeMode();
        });
    };

    const editPCon = () => {
      this.offlinePconService.editPcon(diagramElement.dataItem.id);
    };

    const deletePCon = () => {
      this.offlinePconService.deletePcon(diagramElement.dataItem);
    };

    const lockPCon = () => {
      this.offlinePconService.lockPcon(diagramElement.dataItem.id);
    };

    const lockOutgoingPCon = () => {
      this.offlinePeerConnectionApiService.lockOutgoingPCon(diagramElement.dataItem.id).then(() => {
        this.refreshDiagram();
      });
    };

    const unlockPCon = () => {
      this.offlinePconService.unlockPcon(diagramElement.dataItem.id);
    };

    const testRoute = () => {
      this.offlinePconService.testRoute(diagramElement.dataItem.id);
    };

    const editVirtualPCon = () => {
      this.offlinePconService.editVirtualPCon(diagramElement.dataItem.id);
    };

    const collapseNode = () => {
      diagramApi.createCluster(diagramElement.id, 'voipPeer', 'cluster');
    };

    function clusterSelectedVoipPeers(): void {
      const selectedVoipPeers = diagramApi.getShapeSelection().filter((selectedShape) => {
        return selectedShape.shapeType === 'voipPeer';
      });

      const selectedVoipPeersIds = selectedVoipPeers.map((voipPeer) => {
        return voipPeer.id;
      });

      diagramApi.clusterByIds(selectedVoipPeersIds, 'cluster');
    }

    function addToCluster(): void {
      const selectedVoipPeers = diagramApi.getShapeSelection().filter((selectedShape) => {
        return selectedShape.shapeType === 'voipPeer';
      }).map((voipPeer) => {
        return voipPeer.id;
      });

      const selectedCluster = diagramApi.getShapeSelection().find((selectedShape) => {
        return selectedShape.shapeType === 'cluster';
      });

      diagramApi.addNodesToCluster(selectedCluster.id, selectedVoipPeers);
    }

    const editCluster = () => {
      const onSuccess = (cluster) => {
        const clusterLabel = this.dataItemToVisualsMappersOfflineService.prepareLabelForDiagram(cluster.name);
        diagramApi.updateCluster(cluster.id, cluster.name, clusterLabel);
      };
      this.offlineNetworkService.editCluster(diagramElement, onSuccess);
    };

    function expandNode(): void {
      diagramApi.openCluster(diagramElement.id);
    }

    // actions support methods

    function isLockEnabled(): boolean {
      return diagramElement.dataItem.adminState.toLowerCase() === 'unlocked';
    }

    function isUnlockEnabled(): boolean {
      return diagramElement.dataItem.adminState.toLowerCase() === 'locked' ||
        diagramElement.dataItem.adminState.toLowerCase() === 'locked_incoming' ||
        diagramElement.dataItem.adminState.toLowerCase() === 'locked_outgoing';
    }

    function isDeleteVoipPeerEnabled(): boolean {
      return !isVoipPeerConnected(diagramElement.dataItem);
    }

    function isVoipPeerConnected(dataItem): boolean {
      const pConsOfVoipPeer = diagramApi.getData().edgesArray.filter((edge) => {

        return edge.connectionType === 'peerConnection' && edge.dataItem.voipPeerId === dataItem.id;
      });
      return pConsOfVoipPeer.length > 0;
    }

    function isClusterSelectedVoipPeersEnabled(): boolean {
      const selectedVoipPeers = diagramApi.getShapeSelection().filter((selectedShape) => {
        return selectedShape.shapeType === 'voipPeer';
      });

      return selectedVoipPeers.length > 1;
    }

    function isAddToClusterEnabled(): boolean {
      const selectedVoipPeers = diagramApi.getShapeSelection().filter((selectedShape) => {
        return selectedShape.shapeType === 'voipPeer';
      });

      const selectedClusters = diagramApi.getShapeSelection().filter((selectedShape) => {
        return selectedShape.shapeType === 'cluster';
      });

      return selectedClusters.length === 1 && selectedVoipPeers.length > 0;
    }

    function updateNodeActionsSupport(): void {
      actionsObject.node.some((action, i) => {
        if (action.actionName === 'lockNode') {
          action.visible = isLockEnabled();
          actionsObject.node[i + 1].visible = action.visible;
          return action.actionName === 'lockNode';
        }
      });
      actionsObject.node.some((action, i) => {
        if (action.actionName === 'unlockNode') {
          action.visible = isUnlockEnabled();
          actionsObject.node[i + 1].visible = action.visible;
          return action.actionName === 'unlockNode';
        }
      });
      actionsObject.node.some((action, i) => {
        if (action.actionName === 'collapseNode') {
          action.visible = isNodeCollapsible();
          actionsObject.node[i + 1].visible = action.visible;
          return action.actionName === 'collapseNode';
        }
      });
    }

    function updateThirdPartyActionsSupport(): void {
      actionsObject.thirdPartyNode.some((action, i) => {
        if (action.actionName === 'lockNode') {
          action.visible = isLockEnabled();
          actionsObject.thirdPartyNode[i + 1].visible = action.visible;
          return action.actionName === 'lockNode';
        }
      });
      actionsObject.thirdPartyNode.some((action, i) => {
        if (action.actionName === 'unlockNode') {
          action.visible = isUnlockEnabled();
          actionsObject.thirdPartyNode[i + 1].visible = action.visible;
          return action.actionName === 'unlockNode';
        }
      });
      actionsObject.thirdPartyNode.some((action, i) => {
        if (action.actionName === 'collapseNode') {
          action.visible = isNodeCollapsible();
          actionsObject.thirdPartyNode[i + 1].visible = action.visible;
          return action.actionName === 'collapseNode';
        }
      });
    }

    function isNodeCollapsible(): boolean {
      let pCons;
      let connectedToNodepCons;
      let connectedToNodeVoipPeersIds;
      let notClusteredVoipPeers;

      pCons = diagramApi.getData().edgesArray.filter((shape) => {
        return shape.connectionType === 'peerConnection';
      });

      connectedToNodepCons = pCons.filter((pCon) => {
        return pCon.dataItem.nodeId === diagramElement.dataItem.id;
      });

      connectedToNodeVoipPeersIds = connectedToNodepCons.map((pCon) => {
        return 'voippeer_' + pCon.dataItem.voipPeerId;
      });

      notClusteredVoipPeers = connectedToNodeVoipPeersIds.filter((voipPeerId) => {
        return diagramApi.findNode(voipPeerId).length === 1;
      });

      return notClusteredVoipPeers.length > 1;
    }

    function updateVoipPeerActionsSupport(): void {
      actionsObject.voipPeer.some((action, i) => {
        if (action.actionName === 'deleteVoipPeer') {
          action.visible = isDeleteVoipPeerEnabled();
          actionsObject.voipPeer[i + 1].visible = action.visible;
          return action.actionName === 'deleteVoipPeer';
        }
      });
      actionsObject.voipPeer.some((action, i) => {
        if (action.actionName === 'clusterSelectedVoipPeers') {
          action.visible = isClusterSelectedVoipPeersEnabled();
          actionsObject.voipPeer[i + 1].visible = action.visible;
          return action.actionName === 'clusterSelectedVoipPeers';
        }
      });
      actionsObject.voipPeer.some((action, i) => {
        if (action.actionName === 'addToCluster') {
          action.visible = isAddToClusterEnabled();
          actionsObject.voipPeer[i + 1].visible = action.visible;
          return action.actionName === 'addToCluster';
        }
      });
    }

    const updatePConActionsSupport = () => {
      actionsObject.peerConnection.some((action, i) => {
        if (action.actionName === 'unlockPCon') {
          action.disabled = !this.isAuthorizedUser() || isLockEnabled();
          return action.disabled === 'unlockPCon';
        }
      });
    };

    const updateVirtualPConActionsSupport = () => {
      actionsObject.virtualPCon.some((action, i) => {
        if (action.actionName === 'lockOutgoingPCon') {
          action.visible = !this.isAuthorizedUser() || isLockEnabled();
          actionsObject.virtualPCon[i + 1].visible = action.visible;
          return action.actionName === 'lockOutgoingPCon';
        }
      });
      actionsObject.virtualPCon.some((action, i) => {
        if (action.actionName === 'unlockPCon') {
          action.visible = isUnlockEnabled();
          actionsObject.virtualPCon[i + 1].visible = action.visible;
          return action.actionName === 'unlockPCon';
        }
      });
    };

    actionsObject = {
      currentElement: 'node',
      node: [
        {
          actionName: 'dragConnection',
          click: dragConnection,
          visible: true,
          disabled: !this.isAuthorizedUser(),
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-arrow-right fa-lg"></i></div><span>Drag connection</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'dragPeerConnection',
          click: dragConnection,
          visible: true,
          disabled: !this.isAuthorizedUser(),
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-arrow-right fa-lg"></i></div><span>Drag peer connection</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'addConnection',
          click: addConnection,
          visible: true,
          disabled: !this.isAuthorizedUser(),
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-arrows-h fa-lg"></i></div><span>Add connection</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'editNode',
          click: editNode,
          visible: true,
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-pencil-square-o fa-lg"></i></div><span>Edit</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'lockNode',
          click: lockNode,
          disabled: !this.isAuthorizedUser(),
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-lock fa-lg"></i></div><span>Lock</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'unlockNode',
          click: unlockNode,
          disabled: !this.isAuthorizedUser(),
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-unlock fa-lg"></i></div><span>Unlock</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'deleteNode',
          click: deleteNode,
          disabled: !this.isAuthorizedUser(),
          visible: true,
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-times fa-lg"></i></div><span>Delete</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'collapseNode',
          click: collapseNode,
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-compress fa-lg"></i></div><span>Collapse</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'exportNodeToTopology',
          click: exportNodeToTopology,
          disabled: !this.isAuthorizedUser(),
          visible: true,
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-external-link"></i></div><span>Export node</span></div>'
        },
        {divider: true, visible: true}

      ],
      thirdPartyNode: [
        {
          actionName: 'dragConnection',
          click: dragConnection,
          visible: true,
          disabled: !this.isAuthorizedUser(),
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-arrow-right fa-lg"></i></div><span>Drag connection</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'dragPeerConnection',
          click: dragConnection,
          visible: true,
          disabled: !this.isAuthorizedUser(),
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-arrow-right fa-lg"></i></div><span>Drag peer connection</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'addConnection',
          click: addConnection,
          visible: true,
          disabled: !this.isAuthorizedUser(),
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-arrows-h fa-lg"></i></div><span>Add connection</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'editNode',
          click: editNode,
          visible: true,
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-pencil-square-o fa-lg"></i></div><span>Edit</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'lockNode',
          click: lockNode,
          disabled: !this.isAuthorizedUser(),
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-lock fa-lg"></i></div><span>Lock</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'unlockNode',
          click: unlockNode,
          disabled: !this.isAuthorizedUser(),
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-unlock fa-lg"></i></div><span>Unlock</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'deleteNode',
          click: deleteNode,
          disabled: !this.isAuthorizedUser(),
          visible: true,
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-times fa-lg"></i></div><span>Delete</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'collapseNode',
          click: collapseNode,
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-compress fa-lg"></i></div><span>Collapse</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'addPCon',
          click: addPCon,
          visible: true,
          disabled: !this.isAuthorizedUser(),
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-plus fa-lg"></i></div><span>Add peer connection</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'exportNodeToTopology',
          click: exportNodeToTopology,
          disabled: !this.isAuthorizedUser(),
          visible: true,
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-external-link"></i></div><span>Export Node</span></div>'
        },
        {divider: true, visible: true}
      ],
      voipPeer: [
        {
          actionName: 'editVoipPeer',
          click: editVoipPeer,
          visible: true,
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-pencil-square-o fa-lg"></i></div><span>Edit</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'deleteVoipPeer',
          click: deleteVoipPeer,
          disabled: !this.isAuthorizedUser(),
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-times fa-lg"></i></div><span>Delete</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'clusterSelectedVoipPeers',
          click: clusterSelectedVoipPeers,
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-compress fa-lg"></i></div><span>Collapse VoIP peers</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'addToCluster',
          click: addToCluster,
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-compress fa-lg"></i></div><span>Add to cluster</span></div>'
        },
        {divider: true, visible: true}
      ],
      connection: [
        {
          actionName: 'editConnection',
          click: editConnection,
          visible: true,
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-pencil-square-o fa-lg"></i></div><span>Edit</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'deleteConnection',
          click: deleteConnection,
          disabled: !this.isAuthorizedUser(),
          visible: true,
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-times fa-lg"></i></div><span>Delete</span></div>'
        },
        {divider: true, visible: true}
      ],
      peerConnection: [
        {
          actionName: 'editPCon',
          click: editPCon,
          visible: true,
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-pencil-square-o fa-lg"></i></div><span>Edit</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'deletePCon',
          click: deletePCon,
          disabled: !this.isAuthorizedUser(),
          visible: true,
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-times fa-lg"></i></div><span>Delete</span></div>'
        },
        {divider: true, visible: true},
        {
          subMenu: 'lockSubMenu',
          actionName: 'lockPCon',
          visible: true,
          click: lockPCon,
          disabled: !this.isAuthorizedUser(),
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-lock fa-lg"></i></div><span>Lock</span></div>'
        },
        {
          actionName: 'unlockPCon',
          visible: true,
          click: unlockPCon,
          disabled: !this.isAuthorizedUser(),
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-unlock fa-lg"></i></div><span>Unlock</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'testRoute',
          click: testRoute,
          visible: false,
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-location-arrow fa-lg"></i></div><span>Test route</span></div>'
        },
        {divider: true, visible: true}
      ],
      virtualPCon: [
        {
          actionName: 'editVirtualPCon',
          click: editVirtualPCon,
          visible: true,
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-pencil-square-o fa-lg"></i></div><span>Edit</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'deletePCon',
          click: deletePCon,
          disabled: !this.isAuthorizedUser(),
          visible: true,
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-times fa-lg"></i></div><span>Delete</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'lockOutgoingPCon',
          click: lockOutgoingPCon,
          disabled: !this.isAuthorizedUser(),
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-lock fa-lg"></i></div><span>Lock Outgoing</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'unlockPCon',
          click: unlockPCon,
          disabled: !this.isAuthorizedUser(),
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-unlock fa-lg"></i></div><span>Unlock</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'testRoute',
          click: testRoute,
          visible: false,
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-location-arrow fa-lg"></i></div><span>Test route</span></div>'
        },
        {divider: true, visible: true}
      ],
      cluster: [
        {
          actionName: 'expandNode',
          click: expandNode,
          visible: true,
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-expand fa-lg"></i></div><span>Expand</span></div>'
        },
        {divider: true, visible: true},
        {
          actionName: 'editCluster',
          click: editCluster,
          visible: true,
          html: '<div class="diagram-context-menu-item"><div class="diagram-action-icon">' +
            '<i class="fa fa-pencil-square-o fa-lg"></i></div><span>Edit</span></div>'
        },
        {divider: true, visible: true},

      ],
      edgesCluster: []
    };

    updateActionsSupport();

    return actionsObject[actionsObject.currentElement];
  }

  prepareLockPeerConnectionSubMenu(clickedElement): any {
    const lockPCon = () => {
      this.offlinePeerConnectionApiService.lockPCon(clickedElement.dataItem.id).then(() => this.refreshDiagram());
    };

    const lockIncomingPCon = () => {
      this.offlinePeerConnectionApiService.lockIncomingPCon(clickedElement.dataItem.id).then(() => this.refreshDiagram());
    };

    const lockOutgoingPCon = () => {
      this.offlinePeerConnectionApiService.lockOutgoingPCon(clickedElement.dataItem.id).then(() => this.refreshDiagram());
    };

    return [
      {
        disabled: !this.isAuthorizedUser() || this.isLocked(clickedElement),
        actionName: 'lockPCon',
        visible: true,
        click: lockPCon,
        html: '<span>Lock</span>'
      },
      {
        disabled: !this.isAuthorizedUser() || this.isIncomingLocked(clickedElement),
        actionName: 'incomingLockedPCon',
        visible: true,
        click: lockIncomingPCon,
        html: '<span>Lock Incoming</span>'
      },
      {
        disabled: !this.isAuthorizedUser() || this.isOutgoingLocked(clickedElement),
        actionName: 'outgoingLockedPCon',
        visible: true,
        click: lockOutgoingPCon,
        html: '<span>Lock Outgoing</span>'
      }
    ];
  }

  private isLocked(clickedElement): boolean {
    return clickedElement.dataItem.adminState.toLowerCase() === 'locked';
  }

  private isIncomingLocked(clickedElement): boolean {
    return clickedElement.dataItem.adminState.toLowerCase() === 'locked_incoming';
  }

  private isOutgoingLocked(clickedElement): boolean {
    return clickedElement.dataItem.adminState.toLowerCase() === 'locked_outgoing';
  }

  private refreshDiagram(): void {
    this.offlineMapService.refresh();
  }

  private isAuthorizedUser(): boolean {
    return this.userPrivilege.isAuthorized();
  }
}
